<template>
  <v-app class="auth-page auth-page--neutral">
    <v-container fluid class="auth-split__panel">
      <v-card class="auth-card" elevation="0">
        <div class="auth-card__header text-center">
          <v-img :src="logo" alt="BRPoints" class="auth-card__logo mx-auto" contain />
          <h1 class="auth-card__title">
            {{ sessionInfo?.reRegistration ? "Atualização de senha" : "Alteração de senha" }}
          </h1>
          <p class="auth-card__subtitle">
            Siga as etapas para
            {{ sessionInfo?.reRegistration ? "atualizar" : "recuperar" }}
            sua senha com segurança.
          </p>
        </div>

        <v-card-text class="auth-card__body">
          <div class="auth-steps" aria-hidden="true">
            <span
              class="auth-steps__item"
              :class="{ 'auth-steps__item--active': currentStep === 'sendOtp', 'auth-steps__item--done': stepIndex > 0 }"
            />
            <span
              class="auth-steps__item"
              :class="{ 'auth-steps__item--active': currentStep === 'validateOtp', 'auth-steps__item--done': stepIndex > 1 }"
            />
            <span
              class="auth-steps__item"
              :class="{ 'auth-steps__item--active': currentStep === 'changePassword' }"
            />
          </div>

          <v-form v-if="currentStep === 'sendOtp'" ref="formSendOtp" v-model="valid" @submit.prevent="handleSendOtp">
            <p class="forgot-step-label">
              {{ sessionInfo?.reRegistration ? "Confirme seu e-mail" : "Informe seu e-mail cadastrado" }}
            </p>
            <v-text-field
              v-model="email"
              label="Email"
              outlined
              dense
              class="br-form-field"
              prepend-inner-icon="mdi-email-outline"
              :rules="ruleEmail"
            />
            <v-btn type="submit" :disabled="!valid" :dark="valid" color="primary" depressed block large class="mt-2">
              Enviar email
            </v-btn>
          </v-form>

          <v-form v-if="currentStep === 'validateOtp'" ref="formValidateOtp" v-model="valid" @submit.prevent="handleValidOtp">
            <p class="forgot-step-label">Insira o código recebido no seu email</p>
            <v-text-field
              v-model="code"
              label="Código"
              outlined
              dense
              class="br-form-field"
              prepend-inner-icon="mdi-shield-key-outline"
              :rules="ruleCode"
            />
            <v-btn type="submit" :disabled="!valid" :dark="valid" color="primary" depressed block large class="mt-2">
              Validar código
            </v-btn>
          </v-form>

          <v-form
            v-if="currentStep === 'changePassword'"
            ref="formChangePassword"
            v-model="valid"
            @submit.prevent="handleChangePassword"
          >
            <p class="forgot-step-label">
              {{ sessionInfo?.reRegistration ? "Crie sua nova senha" : "Defina sua nova senha" }}
            </p>
            <v-text-field
              v-model="password"
              label="Nova senha"
              outlined
              dense
              class="br-form-field"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              :rules="rulePassword"
              :type="showPassword ? 'text' : 'password'"
              prepend-inner-icon="mdi-lock-outline"
              @click:append="showPassword = !showPassword"
            />
            <v-btn type="submit" :disabled="!valid" :dark="valid" color="primary" depressed block large class="mt-2">
              Alterar senha
            </v-btn>
          </v-form>

          <v-btn text block color="primary" class="mt-4" @click="handleBack">
            Voltar para login
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { SendOtp } from "@/services/user/SendOtp";
import { ValidateOtp } from "@/services/user/ValidateOtp";
import { ResetPassword } from "@/services/user/ResetPassword";
import { mapState } from "vuex";

export default defineComponent({
  data() {
    return {
      valid: false,
      currentStep: "sendOtp",
      showPassword: false,
      email: "",
      code: "",
      document: "",
      name: "",
      password: "",
      ruleEmail: [(value: string) => !!value || "O campo é obrigatório"],
      ruleCode: [(value: string) => !!value || "O campo é obrigatório"],
      rulePassword: [(value: string) => !!value || "O campo é obrigatório"],
      logo: require("../../assets/logo-login.jpg"),
    };
  },
  computed: {
    ...mapState(["sessionInfo"]),
    stepIndex(): number {
      if (this.currentStep === "sendOtp") return 0;
      if (this.currentStep === "validateOtp") return 1;
      return 2;
    },
  },
  methods: {
    async handleSendOtp() {
      const sendOtp = new SendOtp();
      await sendOtp
        .execute(this.email)
        .then((res) => {
          if (res === 201) {
            this.currentStep = "validateOtp";
          } else {
            this.$store.dispatch("showSnack", {
              message: "Erro ao validar a conta",
              color: "red",
            });
          }
        })
        .catch(() => {
          this.$store.dispatch("showSnack", {
            message: "Erro ao encontrar a conta",
            color: "red",
          });
        });
    },
    async handleValidOtp() {
      const validateOtp = new ValidateOtp();
      await validateOtp
        .execute(this.email, this.code)
        .then((res) => {
          if (res.data.isValid) {
            this.document = res.data.document;
            this.currentStep = "changePassword";
          } else {
            this.$store.dispatch("showSnack", {
              message: "Erro ao validar o código",
              color: "red",
            });
          }
        })
        .catch(() => {
          this.$store.dispatch("showSnack", {
            message: "Erro ao validar o código",
            color: "red",
          });
        });
    },
    async handleChangePassword() {
      const resetPassword = new ResetPassword();
      await resetPassword
        .execute(this.document, this.code, this.password, this.name)
        .then((res) => {
          if (res.data.success) {
            this.$store.dispatch("showSnack", {
              message: "Senha alterada com sucesso",
              color: "green",
            });
            this.$router.push("/login");
          } else {
            this.$store.dispatch("showSnack", {
              message: "Erro ao alterar a senha",
              color: "red",
            });
          }
        })
        .catch(() => {
          this.$store.dispatch("showSnack", {
            message: "Erro ao alterar a senha",
            color: "red",
          });
        });
    },
    handleBack() {
      this.$router.push("/login");
    },
  },
  async mounted() {
    if (this.sessionInfo?.reRegistration) {
      this.currentStep = "validateOtp";
      this.email = this.sessionInfo.email;
      this.name = this.sessionInfo.name;
      const sendOtp = new SendOtp();
      await sendOtp
        .execute(this.sessionInfo?.email)
        .then((res) => {
          if (res !== 201) {
            this.$store.dispatch("showSnack", {
              message: "Erro ao validar a conta",
              color: "red",
            });
          }
        })
        .catch(() => {
          this.$store.dispatch("showSnack", {
            message: "Erro ao encontrar a conta",
            color: "red",
          });
        });
    }
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.forgot-step-label {
  margin: 0 0 12px;
  font-size: $br-font-size-sm;
  font-weight: 600;
  color: $br-muted;
}
</style>
